import React, { useEffect, useRef, useState } from 'react';
import Lottie from 'lottie-react';
import { animated, useSpring } from '@react-spring/web';

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
}

const InfoModal: React.FC<InfoModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
}) => {
  const modal = useRef<HTMLDivElement>(null);
  const [animationData, setAnimationData] = useState<any>(null);

  // 🔁 Cargar animación solo si el modal está visible
  useEffect(() => {
    if (isOpen) {
      import('../../assets/animations/alerts/alerta-info.json').then((data) =>
        setAnimationData(data.default)
      );
    } else {
      setAnimationData(null);
    }
  }, [isOpen]);

  // Click fuera para cerrar
  useEffect(() => {
    const clickHandler = (event: MouseEvent) => {
      if (modal.current && !modal.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', clickHandler);
    return () => document.removeEventListener('mousedown', clickHandler);
  }, [onClose]);

  // Tecla ESC o Enter para cerrar
  useEffect(() => {
    const keyHandler = ({ key }: KeyboardEvent) => {
      if (key === 'Escape' || key === 'Enter') {
        onClose();
      }
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [onClose]);

  // Animación suave
  const animationStyle = useSpring({
    opacity: isOpen ? 1 : 0,
    config: { duration: 300 },
  });

  if (!isOpen) return null;

  return (
    <div className="fixed left-0 top-0 z-[999999] flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5">
      <animated.div
        style={animationStyle}
        ref={modal}
        className="w-full max-w-md rounded-lg bg-white px-8 py-12 text-center dark:bg-boxdark shadow-lg"
      >
        {/* 🔄 Animación solo si se carga */}
        {animationData && (
          <div className="mb-4 flex justify-center">
            <Lottie animationData={animationData} loop={true} style={{ width: 70, height: 70 }} />
          </div>
        )}

        <h3 className="pb-2 text-xl font-bold text-black dark:text-white sm:text-2xl">
          {title}
        </h3>
        <span className="mx-auto mb-6 inline-block h-1 w-22.5 rounded bg-primary"></span>

        <p className="text-gray-800 dark:text-gray-300 mb-6">{message}</p>

        <button
          onClick={onClose}
          className="block w-full rounded border border-primary bg-primary p-3 text-center font-medium text-white transition hover:bg-opacity-90"
        >
          Entendido
        </button>
      </animated.div>
    </div>
  );
};

export default React.memo(InfoModal);
